/**
 * Wire protocol between client and authoritative server.
 *
 * zod gives us one source of truth: the same schema validates untrusted client
 * input on the server AND produces the TypeScript types both sides import.
 * Never trust a client message until it has passed these schemas.
 */
import { z } from "zod";

export const Vec2Schema = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
});

/**
 * Client -> Server: only *inputs*, never results. The server simulates
 * everything (movement, hits, damage); this is the core anti-cheat stance.
 * `seq` lets the server ack inputs and the client replay during reconciliation.
 */
export const InputCommandSchema = z.object({
  seq: z.number().int().nonnegative(),
  /** Client tick this input was produced on. */
  tick: z.number().int().nonnegative(),
  /** Movement stick, magnitude clamped to <= 1 (server re-clamps anyway). */
  move: Vec2Schema,
  /** Aim direction (unit-ish), drives shooting/facing. */
  aim: Vec2Schema,
  fire: z.boolean(),
});
export type InputCommand = z.infer<typeof InputCommandSchema>;

/** One entity's replicated state inside a snapshot. */
export const EntitySnapshotSchema = z.object({
  id: z.number().int().positive(),
  kind: z.enum(["player", "bot", "projectile"]),
  pos: Vec2Schema,
  vel: Vec2Schema,
  health: z.number().optional(),
});
export type EntitySnapshot = z.infer<typeof EntitySnapshotSchema>;

/**
 * Server -> Client: authoritative world state for a tick. `ackSeq` is the last
 * input the server has processed for this client (drives reconciliation).
 */
export const SnapshotSchema = z.object({
  tick: z.number().int().nonnegative(),
  ackSeq: z.number().int().nonnegative(),
  entities: z.array(EntitySnapshotSchema),
});
export type Snapshot = z.infer<typeof SnapshotSchema>;
